import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import {
  Users,
  Mail,
  AlertTriangle,
  UserPlus,
  Link2,
  XCircle,
  Hash
} from "lucide-react";
import type { ContactPerson, Client, UnconfirmedParticipant } from "@shared/schema";

import { SensitivityDot } from "@/lib/sensitivity.tsx";

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'main_contact', label: 'Main contact' },
  { value: 'c_level', label: 'C-Level' },
  { value: 'other', label: 'Other' },
];

type ProjectContactWithContact = {
  id: number;
  projectId: string;
  contactId: string;
  role?: string;
  interactionCount: number;
  firstSeenAt: string | Date;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    sensitivityLevel: number;
    email?: string;
    category?: string | null;
  };
};

interface ProjectParticipantsProps {
  projectId: string;
}

export function ProjectParticipants({ projectId }: ProjectParticipantsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [triageDialogOpen, setTriageDialogOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<UnconfirmedParticipant | null>(null);
  const [triageAction, setTriageAction] = useState<'link' | 'create' | null>(null);
  const [linkContactId, setLinkContactId] = useState('');
  const [newContactForm, setNewContactForm] = useState({ clientId: '', firstName: '', lastName: '', email: '' });
  const [noCompany, setNoCompany] = useState(false);
  const [triageRole, setTriageRole] = useState('other');

  const { data: projectContacts = [], isLoading: contactsLoading } = useQuery<ProjectContactWithContact[]>({
    queryKey: ['/api/projects', projectId, 'contacts'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/projects/${projectId}/contacts`);
      if (!res.ok) throw new Error('Failed to fetch project contacts');
      return res.json();
    },
    enabled: !!projectId,
  });

  const { data: unconfirmedParticipants = [] } = useQuery<UnconfirmedParticipant[]>({
    queryKey: ['/api/projects', projectId, 'unconfirmed-participants'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/projects/${projectId}/unconfirmed-participants`);
      if (!res.ok) throw new Error('Failed to fetch unconfirmed participants');
      return res.json();
    },
    enabled: !!projectId, // task #40: all authenticated users can triage participants
  });

  const { data: allContacts = [] } = useQuery<ContactPerson[]>({
    queryKey: ['/api/contact-persons'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/contact-persons');
      if (!res.ok) throw new Error('Failed to fetch contacts');
      return res.json();
    },
    enabled: triageDialogOpen,
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ['/api/clients'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/clients');
      if (!res.ok) throw new Error('Failed to fetch clients');
      return res.json();
    },
    enabled: triageDialogOpen && triageAction === 'create',
  });

  // Task #40: edit a linked contact's project role
  const roleMutation = useMutation({
    mutationFn: async ({ contactId, role }: { contactId: string; role: string }) => {
      const res = await apiRequest('PATCH', `/api/projects/${projectId}/contacts/${contactId}`, { role });
      if (!res.ok) throw new Error('Failed to update role');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'contacts'] });
      toast({ title: "Role updated" });
    },
    onError: () => toast({ title: "Failed to update role", variant: "destructive" }),
  });

  const triageMutation = useMutation({
    mutationFn: async ({ participantId, ...body }: { participantId: number } & Record<string, unknown>) => {
      const res = await apiRequest(
        'POST',
        `/api/projects/${projectId}/unconfirmed-participants/${participantId}/action`,
        body
      );
      if (!res.ok) throw new Error('Failed to process action');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'contacts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'unconfirmed-participants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/contact-persons'] });
      setTriageDialogOpen(false);
      setSelectedParticipant(null);
      setTriageAction(null);
      toast({ title: "Participant processed successfully" });
    },
    onError: () => {
      toast({ title: "Failed to process participant", variant: "destructive" });
    },
  });

  const handleDismiss = (p: UnconfirmedParticipant) => {
    triageMutation.mutate({ participantId: p.id, action: 'dismiss' });
  };

  const openTriageDialog = (p: UnconfirmedParticipant) => {
    setSelectedParticipant(p);
    setTriageAction(null);
    setLinkContactId('');
    setNewContactForm({ clientId: '', firstName: '', lastName: '', email: p.emailAddress });
    setNoCompany(false);
    setTriageRole('other');
    setTriageDialogOpen(true);
  };

  const handleTriageSubmit = () => {
    if (!selectedParticipant) return;
    if (triageAction === 'link') {
      triageMutation.mutate({ participantId: selectedParticipant.id, action: 'link', contactId: linkContactId, role: triageRole });
    } else if (triageAction === 'create') {
      triageMutation.mutate({
        participantId: selectedParticipant.id,
        action: 'create',
        ...newContactForm,
        clientId: noCompany ? '' : newContactForm.clientId,
        role: triageRole,
      });
    }
  };

  if (contactsLoading) {
    return <div className="text-gray-400 text-sm py-2">Loading participants...</div>;
  }

  const hasContacts = projectContacts.length > 0;
  const hasUnconfirmed = unconfirmedParticipants.length > 0;

  if (!hasContacts && !hasUnconfirmed) {
    return (
      <div className="text-sm text-gray-500 py-3 text-center border border-dashed border-gray-600 rounded">
        <Users className="w-4 h-4 mx-auto mb-1 text-gray-600" />
        No participants linked yet
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {hasUnconfirmed && (
        <div className="p-2 rounded border border-yellow-700/50 bg-yellow-900/20">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
            <span className="text-xs font-medium text-yellow-400">
              {unconfirmedParticipants.length} unconfirmed participant{unconfirmedParticipants.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-1">
            {unconfirmedParticipants.map(p => (
              <div key={p.id} className="flex items-center justify-between py-1 px-2 rounded bg-gray-800/50 text-xs">
                <div className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-300">{p.emailAddress}</span>
                  {p.displayName && <span className="text-gray-500">({p.displayName})</span>}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-1.5 text-[10px] text-blue-400 hover:text-blue-300"
                    onClick={() => openTriageDialog(p)}
                  >
                    <UserPlus className="w-3 h-3 mr-0.5" />
                    Resolve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-1.5 text-[10px] text-gray-500 hover:text-gray-300"
                    onClick={() => handleDismiss(p)}
                    disabled={triageMutation.isPending}
                  >
                    <XCircle className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasContacts && (
        <div className="space-y-1">
          {projectContacts.map(pc => (
            <div key={pc.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-800/30 text-xs">
              <div className="flex items-center gap-2">
                <SensitivityDot level={pc.contact.sensitivityLevel ?? 0} />
                <span className="text-white font-medium">
                  {pc.contact.firstName} {pc.contact.lastName}
                </span>
                {isAdmin && pc.contact.email && (
                  <span className="text-gray-500">{pc.contact.email}</span>
                )}
                {isAdmin && pc.contact.category && (
                  <Badge variant="outline" className="text-[9px] h-4 border-gray-600 text-gray-500">
                    {pc.contact.category}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Task #40: editable project role */}
                <Select
                  value={pc.role || 'other'}
                  onValueChange={(v) => roleMutation.mutate({ contactId: pc.contactId, role: v })}
                >
                  <SelectTrigger className="h-5 w-[118px] px-1.5 text-[10px] navy-bg border-gray-600 text-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 text-gray-500">
                  <Hash className="w-3 h-3" />
                  <span>{pc.interactionCount}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {triageDialogOpen && selectedParticipant && (
        <Dialog open={triageDialogOpen} onOpenChange={setTriageDialogOpen}>
          <DialogContent className="bg-gray-800 text-white border-gray-700">
            <DialogHeader>
              <DialogTitle>Resolve: {selectedParticipant.emailAddress}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={triageAction === 'link' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTriageAction('link')}
                  className={triageAction === 'link' ? 'bg-blue-600' : ''}
                >
                  <Link2 className="w-3 h-3 mr-1" />
                  Link to Existing
                </Button>
                <Button
                  variant={triageAction === 'create' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTriageAction('create')}
                  className={triageAction === 'create' ? 'bg-blue-600' : ''}
                >
                  <UserPlus className="w-3 h-3 mr-1" />
                  Create New
                </Button>
              </div>

              {triageAction === 'link' && (
                <div>
                  <Label>Select Contact</Label>
                  <Select value={linkContactId} onValueChange={setLinkContactId}>
                    <SelectTrigger className="navy-bg border-gray-600 text-white">
                      <SelectValue placeholder="Choose a contact..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allContacts.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.firstName} {c.lastName} ({c.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {triageAction === 'create' && (
                <div className="space-y-3">
                  <div>
                    <Label>Company</Label>
                    <Select
                      value={noCompany ? '' : newContactForm.clientId}
                      onValueChange={v => { setNewContactForm(p => ({ ...p, clientId: v })); setNoCompany(false); }}
                      disabled={noCompany}
                    >
                      <SelectTrigger className="navy-bg border-gray-600 text-white">
                        <SelectValue placeholder="Select company..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.filter(c => c.isActive).map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.companyName} ({c.id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer mt-1.5">
                      <input type="checkbox" checked={noCompany} onChange={e => setNoCompany(e.target.checked)} />
                      Not related to a company
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={newContactForm.firstName}
                        onChange={e => setNewContactForm(p => ({ ...p, firstName: e.target.value }))}
                        className="navy-bg border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={newContactForm.lastName}
                        onChange={e => setNewContactForm(p => ({ ...p, lastName: e.target.value }))}
                        className="navy-bg border-gray-600 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={newContactForm.email}
                      onChange={e => setNewContactForm(p => ({ ...p, email: e.target.value }))}
                      className="navy-bg border-gray-600 text-white"
                    />
                  </div>
                </div>
              )}

              {triageAction && (
                <div>
                  <Label>Project role</Label>
                  <Select value={triageRole} onValueChange={setTriageRole}>
                    <SelectTrigger className="navy-bg border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="c_level">C-Level (Geschäftsleitung, Abteilungsleitung, o.ä.)</SelectItem>
                      <SelectItem value="main_contact">Main project contact</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTriageDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleTriageSubmit}
                disabled={
                  !triageAction ||
                  (triageAction === 'link' && !linkContactId) ||
                  (triageAction === 'create' && ((!noCompany && !newContactForm.clientId) || !newContactForm.firstName || !newContactForm.lastName || !newContactForm.email)) ||
                  triageMutation.isPending
                }
                className="bg-blue-600 hover:bg-blue-700"
              >
                {triageMutation.isPending ? 'Processing...' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
